import type { PublishedInteraction } from '@quizee/core';
import { readDomains } from './domains';
import type { Assert, Extends } from './types';
import { BRAND } from '@quizee/core/tokens';

/**
 * The published document, as the widget reads it.
 *
 * These are hand written structural types rather than the zod inferred ones, because importing the
 * schema would drag zod into the bundle. `_NoDrift` below is what keeps the two honest: if the
 * published schema stops satisfying this shape, the widget fails to compile.
 */
export interface LiveOption {
  readonly id: string;
  readonly label: string;
  readonly image_url?: string | undefined;
  /** Trivia only. Carried in the published document because instant feedback happens client side. */
  readonly is_correct?: boolean | undefined;
  /** Personality only. How far this option pushes the reader towards each outcome. */
  readonly outcome_weights?: Readonly<Record<string, number>> | undefined;
}

export interface LiveScale {
  readonly min: number;
  readonly max: number;
  readonly min_label?: string | undefined;
  readonly max_label?: string | undefined;
}

export interface LiveQuestion {
  readonly id: string;
  readonly type: string;
  readonly prompt: string;
  readonly options: readonly LiveOption[];
  readonly required: boolean;
  readonly image_url?: string | undefined;
  readonly placeholder?: string | undefined;
  /** Free text only. Absent means the publisher did not say, so the format decides. */
  readonly multiline?: boolean | undefined;
  readonly scale?: LiveScale | undefined;
}

export interface LiveOutcome {
  readonly id: string;
  readonly title: string;
  readonly description?: string | undefined;
  readonly image_url?: string | undefined;
}

/**
 * One run of the consent notice, as the widget receives it.
 *
 * Data, never markup. The publisher's wording is sanitised on save and converted to segments at
 * publish (see ARCHITECTURE D27), so the widget renders text nodes and an anchor where there is an
 * href, and needs neither `innerHTML` nor a sanitiser to make `innerHTML` safe. There is room for
 * neither inside a 30KB bundle rendering into a shadow root on somebody else's article.
 */
export interface LiveConsentSegment {
  readonly text: string;
  readonly href?: string | undefined;
  readonly emphasis?: 'strong' | 'em' | undefined;
}

export interface LiveConsent {
  readonly segments: readonly LiveConsentSegment[];
}

/**
 * The optional newsletter step shown once the reader has finished.
 *
 * Optional in both senses: the publisher chooses whether it appears at all, and a reader who
 * ignores it has still completed the interaction. The consent wording is required, because there
 * is nothing to record as agreed to without it.
 *
 * `double_opt_in` is what the publisher configured. What the reader is told afterwards comes from
 * the endpoint's own answer, not from this: see `postSubscription`.
 */
export interface LiveSignup {
  readonly heading: string;
  readonly button_label: string;
  readonly consent: readonly LiveConsentSegment[];
  readonly double_opt_in: boolean;
}

export interface LivePresentation {
  readonly accent_colour: string;
  readonly text_colour: string;
  readonly background_colour: string;
  readonly corner_radius: number;
  readonly font_family?: string | undefined;
  readonly show_results: string;
  readonly show_brand_credit: boolean;
}

export interface LiveEndpoints {
  readonly events: string;
  readonly results?: string | undefined;
  /** Where free text answers go. Carried in the document rather than worked out from the events url. */
  readonly submissions?: string | undefined;
  /** Where a newsletter signup goes, for the same reason and with the same fallback. */
  readonly subscriptions?: string | undefined;
}

export interface LiveConfig {
  readonly interaction_id: string;
  readonly version: number;
  readonly format: string;
  readonly title: string;
  readonly introduction?: string | undefined;
  readonly completion_message?: string | undefined;
  readonly questions: readonly LiveQuestion[];
  readonly outcomes?: readonly LiveOutcome[] | undefined;
  readonly presentation: LivePresentation;
  readonly endpoints: LiveEndpoints;
  /** The notice shown beside the interaction. Absent on documents published before it existed. */
  readonly consent?: LiveConsent | undefined;
  /** Present only when the publisher turned the signup on and gave it consent wording. */
  readonly signup?: LiveSignup | undefined;
  /**
   * Every domain this interaction may render on, the owning brand's and any shared brand's flattened
   * into one list. Absent on documents published before the field existed, and absent means render.
   */
  readonly domains?: readonly string[] | undefined;
}

/**
 * `signup` is left out of the drift check on purpose.
 *
 * Everything else here is the published shape exactly, so the compiler catches the two diverging.
 * The signup is the widget's own normalised view of that block (defaults filled in, an unusable
 * block dropped), and `publishedInteractionSchema` does not carry the field yet. When it does, the
 * two shapes want reconciling here rather than this exclusion being widened.
 */
type _NoDrift = Assert<Extends<PublishedInteraction, Omit<LiveConfig, 'signup'>>>;

export const DEFAULT_PRESENTATION: LivePresentation = {
  accent_colour: BRAND.accent,
  text_colour: BRAND.ink,
  background_colour: BRAND.surface,
  corner_radius: 12,
  show_results: 'after_answer',
  show_brand_credit: false,
};

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;
const INTERACTION_ID = /^int_[0-9a-f]{32}$/;
const QUESTION_ID = /^que_[0-9a-f]{32}$/;
const OPTION_ID = /^opt_[0-9a-f]{32}$/;
const OUTCOME_ID = /^out_[0-9a-f]{32}$/;

/** The question types the engine knows how to render. Anything else is not a question we can show. */
const QUESTION_TYPES = ['single_choice', 'multiple_choice', 'free_text', 'scale'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
}

function httpUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
    return url.toString();
  } catch {
    return undefined;
  }
}

function colour(value: unknown, fallback: string): string {
  return typeof value === 'string' && HEX_COLOUR.test(value) ? value : fallback;
}

/**
 * Presentation is cosmetic, so anything unreadable falls back to the default rather than failing
 * the whole document. Content and endpoints get the opposite treatment below: those we refuse.
 */
function readPresentation(raw: unknown): LivePresentation {
  if (!isRecord(raw)) return DEFAULT_PRESENTATION;

  const radius = raw['corner_radius'];
  const cornerRadius =
    typeof radius === 'number' && Number.isFinite(radius)
      ? Math.min(40, Math.max(0, Math.round(radius)))
      : DEFAULT_PRESENTATION.corner_radius;

  const showResults = raw['show_results'];
  const font = text(raw['font_family'], 200);

  return {
    accent_colour: colour(raw['accent_colour'], DEFAULT_PRESENTATION.accent_colour),
    text_colour: colour(raw['text_colour'], DEFAULT_PRESENTATION.text_colour),
    background_colour: colour(raw['background_colour'], DEFAULT_PRESENTATION.background_colour),
    corner_radius: cornerRadius,
    ...(font === undefined ? {} : { font_family: font }),
    show_results:
      showResults === 'after_answer' ||
      showResults === 'after_completion' ||
      showResults === 'never'
        ? showResults
        : DEFAULT_PRESENTATION.show_results,
    show_brand_credit: raw['show_brand_credit'] === true,
  };
}

/** Weights towards outcomes, keyed by outcome id. Anything unreadable is simply left out. */
function readWeights(raw: unknown): Record<string, number> | undefined {
  if (!isRecord(raw)) return undefined;
  const weights: Record<string, number> = {};
  for (const key of Object.keys(raw)) {
    const value = raw[key];
    if (!OUTCOME_ID.test(key)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    weights[key] = value;
  }
  return Object.keys(weights).length === 0 ? undefined : weights;
}

function readOption(raw: unknown, seen: Set<string>): LiveOption | null {
  if (!isRecord(raw)) return null;
  const id = raw['id'];
  const label = text(raw['label'], 300);
  if (typeof id !== 'string' || !OPTION_ID.test(id) || label === undefined) return null;
  if (seen.has(id)) return null;
  seen.add(id);

  const image = httpUrl(raw['image_url']);
  const correct = raw['is_correct'];
  const weights = readWeights(raw['outcome_weights']);

  return {
    id,
    label,
    ...(image === undefined ? {} : { image_url: image }),
    ...(typeof correct === 'boolean' ? { is_correct: correct } : {}),
    ...(weights === undefined ? {} : { outcome_weights: weights }),
  };
}

function readScale(raw: unknown): LiveScale | null {
  if (!isRecord(raw)) return null;
  const min = raw['min'];
  const max = raw['max'];
  if (typeof min !== 'number' || !Number.isInteger(min)) return null;
  if (typeof max !== 'number' || !Number.isInteger(max)) return null;
  if (min >= max) return null;

  const minLabel = text(raw['min_label'], 60);
  const maxLabel = text(raw['max_label'], 60);
  return {
    min,
    max,
    ...(minLabel === undefined ? {} : { min_label: minLabel }),
    ...(maxLabel === undefined ? {} : { max_label: maxLabel }),
  };
}

function readQuestion(raw: unknown, seenOptions: Set<string>): LiveQuestion | null {
  if (!isRecord(raw)) return null;

  const id = raw['id'];
  if (typeof id !== 'string' || !QUESTION_ID.test(id)) return null;

  const prompt = text(raw['prompt'], 1000);
  if (prompt === undefined) return null;

  const rawType = raw['type'];
  // An unknown type is treated as a single choice question, which then has to satisfy the option
  // rules below or be refused. Guessing is safer than rendering a control we do not understand.
  const type =
    typeof rawType === 'string' && QUESTION_TYPES.includes(rawType) ? rawType : 'single_choice';
  const choice = type === 'single_choice' || type === 'multiple_choice';

  const options: LiveOption[] = [];
  if (choice) {
    const rawOptions = raw['options'];
    if (!Array.isArray(rawOptions)) return null;
    for (const candidate of rawOptions) {
      const option = readOption(candidate, seenOptions);
      if (option !== null) options.push(option);
    }
    if (options.length < 2) return null;
  }

  let scale: LiveScale | null = null;
  if (type === 'scale') {
    scale = readScale(raw['scale']);
    if (scale === null) return null;
  }

  const image = httpUrl(raw['image_url']);
  const placeholder = text(raw['placeholder'], 200);
  const multiline = typeof raw['multiline'] === 'boolean' ? raw['multiline'] : undefined;

  return {
    id,
    type,
    prompt,
    options,
    // The published schema always carries this. Defaulting to required is the safer guess for a
    // document that has somehow lost it: a poll nobody can answer is worse than an extra tap.
    required: raw['required'] !== false,
    ...(image === undefined ? {} : { image_url: image }),
    ...(placeholder === undefined ? {} : { placeholder }),
    ...(multiline === undefined ? {} : { multiline }),
    ...(scale === null ? {} : { scale }),
  };
}

/** Bounds on the notice. Generous for a sentence with a link, mean enough to stay a sentence. */
const MAX_SEGMENTS = 20;
const MAX_SEGMENT_CHARS = 1000;
const MAX_CONSENT_CHARS = 2000;

/**
 * A segment's text, kept exactly as it was written apart from its length.
 *
 * Deliberately not trimmed, which is the one thing that would quietly break a notice: the space
 * before a link is the last character of the run in front of it, so trimming every run turns
 * "See our privacy notice for" into "See ourprivacy noticefor". A run of nothing but a space is
 * therefore kept too.
 *
 * Control characters go, because a newline or a bidi override in a consent notice can only be
 * there to make what is on screen differ from what is stored. The sanitiser removed them already;
 * this is the belt to that pair of braces, and it costs one pass over a sentence.
 */
function segmentText(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;

  let cleaned = '';
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) continue;
    cleaned += character;
    if (cleaned.length >= max) break;
  }
  return cleaned.length === 0 ? undefined : cleaned;
}

/**
 * Read consent segments.
 *
 * Anything unreadable is dropped rather than rendered: a segment with no text says nothing, and a
 * segment whose href is not http or https loses the href and keeps its words, which is what the
 * sanitiser on the way in does too.
 *
 * Segments past the budget are dropped whole rather than truncated part way through a word,
 * because the widget shows and sends the same list. Cutting a run in half would mean recording
 * agreement to wording nobody was shown.
 */
function readSegments(raw: unknown): LiveConsentSegment[] {
  if (!Array.isArray(raw)) return [];

  const segments: LiveConsentSegment[] = [];
  let used = 0;
  for (const candidate of raw) {
    if (segments.length >= MAX_SEGMENTS) break;
    if (!isRecord(candidate)) continue;
    const value = segmentText(candidate['text'], MAX_SEGMENT_CHARS);
    if (value === undefined) continue;
    if (used + value.length > MAX_CONSENT_CHARS) break;
    used += value.length;

    const href = httpUrl(candidate['href']);
    const emphasis = candidate['emphasis'];
    segments.push({
      text: value,
      ...(href === undefined ? {} : { href }),
      ...(emphasis === 'strong' || emphasis === 'em' ? { emphasis } : {}),
    });
  }
  return segments;
}

/** Whether these runs actually say anything. A notice of nothing but spaces is not a notice. */
function saysSomething(segments: readonly LiveConsentSegment[]): boolean {
  return segments.some((segment) => segment.text.trim().length > 0);
}

function readConsent(raw: unknown): LiveConsent | undefined {
  if (!isRecord(raw)) return undefined;
  const segments = readSegments(raw['segments']);
  return saysSomething(segments) ? { segments } : undefined;
}

const MAX_HEADING_CHARS = 120;
const MAX_BUTTON_CHARS = 40;

/**
 * Read the signup block.
 *
 * Absent, malformed or switched off all mean the same thing: no step. So does consent wording that
 * did not survive parsing, and that one is deliberate rather than defensive tidiness. Asking for
 * marketing consent means showing the reader what they are agreeing to and recording those exact
 * words, so with nothing to show there is nothing that could honestly be recorded, and the right
 * answer is to leave the step out rather than to invent wording on a publisher's behalf.
 */
function readSignup(raw: unknown): LiveSignup | undefined {
  if (!isRecord(raw) || raw['enabled'] !== true) return undefined;

  const consent = readSegments(raw['consent']);
  if (!saysSomething(consent)) return undefined;

  return {
    heading: text(raw['heading'], MAX_HEADING_CHARS) ?? 'Get our newsletter',
    button_label: text(raw['button_label'], MAX_BUTTON_CHARS) ?? 'Subscribe',
    consent,
    // A missing flag is read as double opt in, which is the claim that cannot mislead: the reader
    // is told a confirmation email is on its way, and one that never arrives is a nuisance rather
    // than a lie about them being subscribed.
    double_opt_in: raw['double_opt_in'] !== false,
  };
}

function readOutcomes(raw: unknown): LiveOutcome[] | undefined {
  if (!Array.isArray(raw)) return undefined;

  const outcomes: LiveOutcome[] = [];
  for (const candidate of raw) {
    if (!isRecord(candidate)) continue;
    const id = candidate['id'];
    const title = text(candidate['title'], 200);
    if (typeof id !== 'string' || !OUTCOME_ID.test(id) || title === undefined) continue;
    if (outcomes.some((existing) => existing.id === id)) continue;
    const description = text(candidate['description'], 2000);
    const image = httpUrl(candidate['image_url']);
    outcomes.push({
      id,
      title,
      ...(description === undefined ? {} : { description }),
      ...(image === undefined ? {} : { image_url: image }),
    });
  }

  return outcomes.length === 0 ? undefined : outcomes;
}

/**
 * Parse the live document by hand.
 *
 * The widget cannot afford a schema library, but it also cannot trust that what came back from the
 * CDN is what it expects: a stale cache entry, a half written publish or an origin serving an
 * error page as JSON all reach this function. Anything the widget cannot render honestly returns
 * null, and the caller hides the embed.
 */
export function parseLiveConfig(raw: unknown): LiveConfig | null {
  if (!isRecord(raw)) return null;

  const interactionId = raw['interaction_id'];
  if (typeof interactionId !== 'string' || !INTERACTION_ID.test(interactionId)) return null;

  const version = raw['version'];
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) return null;

  const title = text(raw['title'], 300);
  if (title === undefined) return null;

  const endpointsRaw = raw['endpoints'];
  if (!isRecord(endpointsRaw)) return null;
  const events = httpUrl(endpointsRaw['events']);
  if (events === undefined) return null;
  const results = httpUrl(endpointsRaw['results']);
  const submissions = httpUrl(endpointsRaw['submissions']);
  const subscriptions = httpUrl(endpointsRaw['subscriptions']);

  const rawQuestions = raw['questions'];
  if (!Array.isArray(rawQuestions) || rawQuestions.length === 0) return null;

  const seenOptions = new Set<string>();
  const seenQuestions = new Set<string>();
  const questions: LiveQuestion[] = [];
  for (const candidate of rawQuestions) {
    const question = readQuestion(candidate, seenOptions);
    if (question === null || seenQuestions.has(question.id)) return null;
    seenQuestions.add(question.id);
    questions.push(question);
  }

  const format = raw['format'];
  const introduction = text(raw['introduction'], 2000);
  const completion = text(raw['completion_message'], 2000);
  const outcomes = readOutcomes(raw['outcomes']);
  const consent = readConsent(raw['consent']);
  const signup = readSignup(raw['signup']);
  const domains = readDomains(raw['domains']);

  return {
    interaction_id: interactionId,
    version,
    format: typeof format === 'string' ? format : 'poll',
    title,
    ...(introduction === undefined ? {} : { introduction }),
    ...(completion === undefined ? {} : { completion_message: completion }),
    questions,
    ...(outcomes === undefined ? {} : { outcomes }),
    presentation: readPresentation(raw['presentation']),
    endpoints: {
      events,
      ...(results === undefined ? {} : { results }),
      ...(submissions === undefined ? {} : { submissions }),
      ...(subscriptions === undefined ? {} : { subscriptions }),
    },
    ...(consent === undefined ? {} : { consent }),
    ...(signup === undefined ? {} : { signup }),
    ...(domains === undefined ? {} : { domains }),
  };
}
